import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
// Material
import { Stack } from "@mui/material";
// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Components
import SelectMultiplePto from "./SelectMultiplePto";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
// API
import { createPtoTypeHandler, deletePtoTypeHandler } from "../../api/PtoModality/mutations";

const getInitialValueOptions = (initialValue) => {
	const response = [];
	let index = 0;
	const travelInitialValueObject = (obj) => {
		for (const element of Object.entries(obj)) {
			const [key, value] = element;
			if (typeof value === "object") {
				travelInitialValueObject(value);
			} else {
				if (["_id"].includes(key)) continue;
				index++;
				response.push({
					label: value,
					order: index,
				});
			}
		}
	};
	travelInitialValueObject(initialValue);
	return response;
};

export default function GroupSelectMultiplePto(props) {
	const queryClient = useQueryClient();
	const location = useSelector((state) => state.location.location);

	// Local State
	const [inProgress, setInProgress] = useState(false);
	const [loading, setLoading] = useState(false);
	const [selectedValue, setSelectedValue] = useState(props.selectedValue);

	/********************************************* MUTATIONS ************************************************/
	const { mutate: addPtoType, isPending: addPtoTypeStatus } = useMutation({
		mutationFn: (input) => createPtoTypeHandler(location.locationId, props.rowTitle, input),
	});
	const { mutate: deletePtoType, isPending: deletePtoTypeStatus } = useMutation({
		mutationFn: (input) => deletePtoTypeHandler(location.locationId, props.rowTitle, input),
	});

	const filteredOptions = useMemo(() => {
		return props.options.filter((item) => {
			return !selectedValue.some((selectedItem) => selectedItem.value === item.label);
		});
	}, [props.options, selectedValue]);

	/********************************************* HELPERS ************************************************/
	const onSettledHandler = (value, item, index) => {
		setLoading(true);
		const valueToStore = {};
		if (item._id) {
			valueToStore["_id"] = item._id;
		}

		valueToStore["value"] = value?.[0]?.label;
		valueToStore["modality"] = value?.[1]?.label;

		switch (value?.[1]?.label) {
			case "Flat":
				valueToStore["modalityDefinition"] = {
					frequency: value?.[2]?.label?.toUpperCase(),
					input: value?.[3]?.input,
					value: !!value?.[3]?.flatValue && value?.[3]?.flatValue > 0 ? value?.[3]?.flatValue : 0,
				};
				break;
			case "Last xQs":
				valueToStore["modalityDefinition"] = {
					value: value?.[2]?.label,
					excludeHolidays: value?.[3]?.excludeHolidays || false,
				};
				break;
			case "Working days proration":
				valueToStore["modalityDefinition"] = {
					excludeHolidays: value?.[2]?.excludeHolidays || false,
				};
				break;
			default:
				break;
		}

		addPtoType(valueToStore, addPtoTypeHandlerCallback(index));
	};

	const addPtoTypeHandlerCallback = (index) => {
		return {
			onSuccess: (res, payload) => {
				setLoading(false);
				const newValue = {
					_id: res._id,
					value: payload.value,
					modality: payload.modality,
				};

				for (const key in payload.modalityDefinition ?? {}) {
					if (Object.hasOwnProperty.call(payload.modalityDefinition, key)) {
						const element = payload.modalityDefinition[key];

						if (element !== null) {
							newValue.modalityDefinition = {
								...newValue.modalityDefinition,
								[key]: element,
							};
						}
					}
				}

				queryClient.setQueryData(["MappedPositionsPto", location?.locationId], (prev) => {
					return prev.map((element) => {
						if (element.systemPosition === props.rowTitle) {
							const newElement = { ...element };
							newElement.ptoType[index] = newValue;
							return newElement;
						}
						return { ...element };
					});
				});
				props.lastXQsIngestionHandler();
				setSelectedValue((prev) => {
					prev[index] = newValue;
					return prev;
				});
			},
			onSettled: () => {
				setInProgress(false);
			},
		};
	};

	const addNewSelector = () => {
		setSelectedValue((prev) => [...prev, {}]);
	};

	const removeSelector = (item, index) => {
		if (item.value) {
			setInProgress(true);
			setLoading(true);
			deletePtoType(item._id, {
				onSuccess: () => {
					setInProgress(false);
					setLoading(false);
					queryClient.setQueryData(["MappedPositionsPto", location?.locationId], (prev) => {
						return prev.map((element) => {
							if (element.systemPosition === props.rowTitle) {
								if (element.ptoType.length === 1) {
									element.ptoType[index] = {};
								} else {
									element.ptoType = element.ptoType.filter((_, i) => i !== index);
								}
							}
							return { ...element };
						});
					});
					setSelectedValue((prev) => {
						if (prev.length === 1) {
							prev[index] = {};
						} else {
							prev = prev.filter((_, i) => i !== index);
						}
						return prev;
					});
					props.lastXQsIngestionHandler();
				},
			});
		} else {
			setInProgress(false);
			setSelectedValue((prev) => [...prev.filter((_, i) => i !== index)]);
		}
	};

	useEffect(() => {
		setSelectedValue(props.selectedValue);
	}, [props.selectedValue]);

	return (
		<Stack direction="row">
			{selectedValue?.map((item, key, arr) => {
				return (
					<Stack key={key} direction="row" alignItems={"center"}>
						<SelectMultiplePto
							title={props.rowTitle}
							disabled={inProgress && !!item?.value}
							initialValue={getInitialValueOptions(item)}
							options={filteredOptions}
							className={"advanced-select regular-input-text"}
							sx={{ width: "250px" }}
							onSettled={(value) => onSettledHandler(value, item, key)}
							loading={addPtoTypeStatus || deletePtoTypeStatus || props.loading}
							information={{
								position: props.rowTitle,
								drawModality: props.drawModality,
							}}
						/>
						{key === arr.length - 1 && (
							<Stack>
								<SimpleIconButton
									className={"btn-add"}
									color={"var(--primary-color)"}
									icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
									iconHover={<AddCircleRounded fontSize="medium" />}
									disabled={inProgress || !item?.value || !filteredOptions.length}
									onClick={addNewSelector}
								/>
							</Stack>
						)}

						{arr.length && (
							<Stack>
								<SimpleDoubleChecking
									className={"btn-delete"}
									disabled={(inProgress && !!item?.value) || loading}
									color={"var(--gray-blue-color)"}
									confirmationButtonColor={"var(--gray-blue-color)"}
									icon={<CloseRoundedIcon fontSize="small" />}
									iconHover={<CloseRoundedIcon fontSize="small" />}
									confirmationIcon={<DeleteOutlineOutlinedIcon fontSize="medium" />}
									onDeleteAction={() => removeSelector(item, key)}
								/>
							</Stack>
						)}
					</Stack>
				);
			})}
		</Stack>
	);
}

GroupSelectMultiplePto.propTypes = {
	options: PropTypes.array,
	rowTitle: PropTypes.string,
	selectedValue: PropTypes.array,
	drawModality: PropTypes.string,
	loading: PropTypes.bool,
	lastXQsIngestionHandler: PropTypes.func,
};
